import { queryOptions, useSuspenseQuery, useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Organization, PostCompanyLogoArgs, UpdateWorkspaceRequest, Workspace, WorkspaceStatus, Company } from './types'
import { tryCatch } from '@/lib/utils/try-catch'
import { createQueryKey, SETTINGS_KEY } from '@/api/query-keys'
import { useSettingsStore } from '../store/useOrganizationStore'
import { axiosInstance } from '../../../api/axios'
import { COMPANY_BASE_KEY } from '@/api/query-keys.ts'
import { useLogout, USER_QUERY_KEY } from '@/feature/auth/api'
import { User, UserResponse } from '@/feature/auth/api/types'
import { useAuthStore } from '@/feature/auth/store/authStore'

type UpdateWorkspaceParams = UpdateWorkspaceRequest & {
  id: string
  status: WorkspaceStatus
}

// USER
type UpdateUserProfilePictureParams = {
  profile_picture_url: File | null
}

export const update_user_profile_picture = async (data: UpdateUserProfilePictureParams) => {
  const formData = new FormData()
  formData.append(`profile_picture`, data.profile_picture_url || ``)
  const response = await axiosInstance.post(`/user/profile-picture`, formData)
  return response.data as UserResponse
}

export const useUpdateUserProfilePictureMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateUserProfilePictureParams) => update_user_profile_picture(data),
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
      useAuthStore.getState().setUser(res)
    }
  })
}

type UpdateUserInfoParams = Partial<User>

const update_user_info = async (data: UpdateUserInfoParams) => {
  const response = await axiosInstance.put(`/user/`, data)
  return response.data as UserResponse
}

export const useUpdateUserInfoMutation = () => {
  const queryClient = useQueryClient()
  const { setUser } = useAuthStore()
  const { setOrganization, setActiveWorkspace } = useSettingsStore()
  return useMutation({
    mutationFn: (data: UpdateUserInfoParams) => update_user_info(data),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      setUser(data)
      setOrganization(data.organizations?.[0] ?? {})
      setActiveWorkspace(data.workspaces?.[0] ?? {})
    }
  })
}

const deleteUser = async (password: string) => {
  const response = await axiosInstance.post(`/user/delete`, {
    password
  })
  return response.data
}

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const logout = useLogout()
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      logout()
      queryClient.clear()
      navigate(`/`)
    }
  })
}
// WORKSPACE

export const getWorkspace = async (workspace_id: string): Promise<Workspace> => {
  const response = await axiosInstance.get(`/workspace/${workspace_id}`)
  return response.data
}

export const updateWorkspace = async (params: UpdateWorkspaceParams): Promise<Workspace> => {
  const { id, ...data } = params
  const response = await axiosInstance.put<Workspace>(`/workspace/${id}`, data)
  return response.data
}

export const useUpdateWorkspaceMutation = () => {
  const queryClient = useQueryClient()
  const { setActiveWorkspace } = useSettingsStore()
  return useMutation({
    mutationFn: (params: UpdateWorkspaceParams) =>
      updateWorkspace({
        ...params,
        status: `active`
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
      queryClient.fetchQuery({ queryKey: USER_QUERY_KEY })
      setActiveWorkspace({
        id: data.id,
        name: data.name,
        status: `active`
      })
    }
  })
}

// COMPANY

export const createCompanyQueryKey = (id: string) => createQueryKey({ baseKey: COMPANY_BASE_KEY, keys: [id] })

export const getCompany = async (id: string): Promise<Company> => {
  const response = await axiosInstance.get<Company>(`/companies/${id}`)
  const company = response.data
  return company
}

export const getCompanyQuery = (companyId: string) => ({
  queryKey: createCompanyQueryKey(companyId),
  queryFn: () => getCompany(companyId),
  enabled: !!companyId
})

// **** GET - company **** //

export const useGetCompany = (companyId: string | null = null) => {
  const { active_company } = useSettingsStore()
  const { data: company, isLoading: isCompanyLoading } = useQuery(getCompanyQuery(companyId ?? active_company?.id!))
  return { company, isLoading: isCompanyLoading }
}

export const createCompany = async (company: Pick<Company, 'name' | 'workspace_id'>) => {
  const response = await axiosInstance.post(`/companies`, company)
  return response.data
}

export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient()
  const { setActiveCompany } = useSettingsStore()
  return useMutation({
    mutationFn: createCompany,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      setActiveCompany(data)
    }
  })
}
export const postCompanyLogo = async ({ companyId, logo }: PostCompanyLogoArgs) => {
  const formData = new FormData()
  formData.append(`logo`, logo)
  const response = await axiosInstance.post<Company>(`/companies/${companyId}/logo`, formData)
  return response.data
}

export const usePostCompanyLogoMutation = () => {
  const queryClient = useQueryClient()
  const { setActiveCompany } = useSettingsStore()
  return useMutation({
    mutationFn: (args: PostCompanyLogoArgs) => postCompanyLogo(args),
    onSuccess: (res, { companyId }) => {
      queryClient.invalidateQueries({
        queryKey: createCompanyQueryKey(companyId)
      })
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      setActiveCompany(res)
    }
  })
}

const updateCompany = async (company: Partial<Company>) => {
  const { id, ...data } = company
  const response = await axiosInstance.put(`/companies/${id}`, data)
  return response.data
}

export const useUpdateCompanyMutation = () => {
  const queryClient = useQueryClient()
  const { setActiveCompany } = useSettingsStore()
  return useMutation({
    mutationFn: updateCompany,
    onSuccess: (_, value) => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      setActiveCompany(value)
    }
  })
}

// ORGANIZATION

export const getOrganization = async (): Promise<Organization[]> => {
  const response = await axiosInstance.get(`/organizations`)
  return response.data
}

const updateOrganization = async (organization: Partial<Organization>) => {
  const { id, ...data } = organization
  const response = await axiosInstance.put(`/organizations/${id}`, data)
  return response.data
}

export const useUpdateOrganizationMutation = () => {
  const queryClient = useQueryClient()
  const { setOrganization } = useSettingsStore()
  return useMutation({
    mutationFn: updateOrganization,
    onSuccess: (_, value) => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      queryClient.invalidateQueries({ queryKey: [`user`] })
      queryClient.fetchQuery({ queryKey: [`user`] })
      setOrganization(value)
    }
  })
}

const postOrganizationLogo = async (organizationId: string, logo: File | null) => {
  const formData = new FormData()
  formData.append(`logo`, logo || ``)
  const response = await axiosInstance.post<Organization>(`/organizations/${organizationId}/logo`, formData)
  return response.data
}

export const usePostOrganizationLogoMutation = (id: string) => {
  const queryClient = useQueryClient()
  const { setOrganization } = useSettingsStore()
  return useMutation({
    mutationFn: (logo: File | null) => postOrganizationLogo(id, logo),
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })
      setOrganization(res)
    }
  })
}

type SettingsResponse = {
  organization: Organization | null
  workspace: Workspace | null
  company: Company | null
}

const getFullSettings = async (): Promise<SettingsResponse> => {
  const { setActiveCompany, setActiveWorkspace, setOrganization } = useSettingsStore.getState()
  const [organizations, organizationsError] = await tryCatch(getOrganization())
  if (organizationsError) {
    return {
      organization: null,
      workspace: null,
      company: null
    }
  }
  setOrganization(organizations[0])
  if (organizations[0].workspace_ids.length === 0) {
    return {
      organization: organizations[0],
      workspace: null,
      company: null
    }
  }
  const [workspace, workspaceError] = await tryCatch(getWorkspace(organizations[0].workspace_ids[0]))
  if (workspaceError) {
    return {
      organization: organizations[0],
      workspace: null,
      company: null
    }
  }
  setActiveWorkspace(workspace)
  if (workspace.company_ids.length === 0) {
    return {
      organization: organizations[0],
      workspace,
      company: null
    }
  }
  const [company, companyError] = await tryCatch(getCompany(workspace.company_ids[0]))
  if (companyError) {
    return {
      organization: organizations[0],
      workspace,
      company: null
    }
  }
  setActiveCompany(company)
  return { organization: organizations[0], workspace, company }
}

export const getFullSettingsQueryOptions = queryOptions({
  queryKey: SETTINGS_KEY,
  queryFn: getFullSettings
})

export const useGetFullSettingsQuery = () => useSuspenseQuery(getFullSettingsQueryOptions)
